// Navigation.context.tsx
import SupportMessageModal from "@components/support/SupportMessageModal";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";

import BaseModal from "@components/modals/BaseModal";
import { navigateTo, registerNavigationHandler } from "./navigation.service";
import BaseNotifModal from "@components/modals/BaseNotifModal";
import SmallSpinner from "@components/ui/loading/SmallSpinner";
import { useNavigate } from "react-router-dom";

export type NavigationPayload = {
  route: string;
  params?: Record<string, any>;
};

type NavigationContextType = {
  showNavigation: (Navigation: NavigationPayload) => void;
};

const NavigationContext = createContext<NavigationContextType | null>(null);

const AUTO_DISMISS_DELAY = 450; // 3 seconds, adjust as needed
export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [Navigation, setNavigation] = useState<NavigationPayload | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const showNavigation = (payload: NavigationPayload) => {
    // Clear existing timeout if any
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new navigation (overwrites previous)
    setNavigation(payload);
    navigate(payload.route);

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      setNavigation(null);
      timeoutRef.current = null;
    }, AUTO_DISMISS_DELAY);
  };

  useEffect(() => {
    registerNavigationHandler(showNavigation);

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const hideNavigation = () => {
    // Clear timeout when manually closed
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setNavigation(null);
  };

  return (
    <NavigationContext.Provider value={{ showNavigation }}>
      {children}

      {false && Navigation && !supportOpen && (
        <BaseNotifModal position="left" onClose={hideNavigation}>
          <div className="flex flex-row gap-4 items-center">
            Navigation <SmallSpinner></SmallSpinner>
          </div>
        </BaseNotifModal>
      )}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const ctx = useContext(NavigationContext);
  if (!ctx) {
    throw new Error("useNavigation must be used inside NavigationProvider");
  }
  return ctx;
};
